import * as Yup from 'yup'
import { TFunction } from 'react-i18next'
import { getChangedValues } from '@percent/utility'
import { Organisation } from '@percent/cause-dashboard/services/cause/causeService.types'
import { SignUpOrganisationDetails } from '@percent/cause-dashboard/services/auth/auth.types'

export type OrganisationDetailsDraft = Required<SignUpOrganisationDetails>

export type OrganisationDetailsErrors = Partial<Record<keyof OrganisationDetailsDraft, string>>

export const addressFields = ['addressLine1', 'addressLine2', 'city', 'state', 'postal'] as const

export const isUsaOrganisation = (organisation: Organisation | null) => organisation?.countryCode === 'USA'

export const fieldLabelKey = (field: keyof OrganisationDetailsDraft, organisation: Organisation | null) =>
  field === 'state' && !isUsaOrganisation(organisation)
    ? ('form.reverification.countyOrState' as const)
    : (`form.reverification.${field}` as const)

export const createOrganisationDraft = (organisation: Organisation | null): OrganisationDetailsDraft => ({
  website: organisation?.website ?? '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postal: ''
})

export const normaliseWebsite = (value: string) => {
  const website = value.trim()

  if (!website || /^[a-z][a-z\d+.-]*:\/\//i.test(website)) {
    return website
  }

  return `https://${website}`
}

export const formatDraftAddress = (draft: OrganisationDetailsDraft) =>
  addressFields
    .map(field => draft[field].trim())
    .filter(Boolean)
    .join(', ')

export const getModifiedOrganisationDetails = (
  draft: OrganisationDetailsDraft,
  organisation: Organisation | null
): SignUpOrganisationDetails => {
  const trimmedDraft = Object.fromEntries(
    Object.entries(draft).map(([field, value]) => [field, value.trim()])
  ) as OrganisationDetailsDraft
  const { website, ...details }: SignUpOrganisationDetails = getChangedValues(
    trimmedDraft,
    createOrganisationDraft(organisation)
  )

  return { ...(website && { website: normaliseWebsite(website) }), ...details }
}

export const organisationDetailsSchema = (t: TFunction, organisation: Organisation | null) => {
  const characterLimitMessage = (field: keyof OrganisationDetailsDraft) =>
    t('form.reverification.characterLimit', { fieldName: t(fieldLabelKey(field, organisation)) })

  const characterLimit = (field: (typeof addressFields)[number]) =>
    Yup.string().min(1, characterLimitMessage(field)).max(255, characterLimitMessage(field)).nullable().trim()

  return Yup.object({
    website: Yup.string()
      .url(t('form.reverification.invalidWebsite'))
      .max(255, characterLimitMessage('website'))
      .nullable(),
    addressLine1: characterLimit('addressLine1'),
    addressLine2: characterLimit('addressLine2'),
    city: characterLimit('city'),
    state: characterLimit('state'),
    postal: characterLimit('postal')
  })
}

export const validateOrganisationDetails = async (
  details: SignUpOrganisationDetails,
  organisation: Organisation | null,
  t: TFunction
): Promise<OrganisationDetailsErrors> => {
  try {
    await organisationDetailsSchema(t, organisation).validate(details, { abortEarly: false })

    return {}
  } catch (error) {
    if (!(error instanceof Yup.ValidationError)) {
      throw error
    }

    return Object.fromEntries(error.inner.map(({ path, message }) => [path, message]))
  }
}
